import React, { ReactNode } from 'react'
import Image from 'next/image'
import LinkedText from './LinkedText'
import { LinkedContent } from '@/utils/ui/types/linked-content'

interface HeroComp2Props {
  mainHeading?: string
  subHeading: string
  image_src: string
  benefits: {
    icon: string
    title: string
    description: string | LinkedContent
  }[]
  orientation?: 'left' | 'right'
}

const HeroComp2: React.FC<HeroComp2Props> = ({
  mainHeading,
  subHeading,
  image_src,
  benefits,
  orientation = 'left',
}) => {
  // Render the description content based on its type
  const renderDescription = (
    description: string | LinkedContent
  ): ReactNode => {
    if (typeof description === 'string') {
      return description
    }

    if (description && typeof description === 'object') {
      // Check if it has part1, link1, etc. structure
      const keys = Object.keys(description)
      const hasStructuredLinks = keys.some((key) =>
        key.match(/^(part|link)\d+$/)
      )

      if (hasStructuredLinks) {
        return <LinkedText content={description} />
      }
    }

    // Fallback
    return String(description)
  }

  return (
    <div className='relative isolate overflow-hidden bg-white'>
      <div className='mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8'>
        <div className='mb-16 text-center'>
          <h1 className='text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl'>
            {mainHeading}
          </h1>
          <h2 className='mt-6 text-xl font-semibold text-gray-600'>
            {subHeading}
          </h2>
        </div>
        <div
          className={`flex flex-col items-center justify-between lg:flex-row ${orientation === 'right' ? 'lg:flex-row-reverse' : ''}`}
        >
          <div className='mb-8 lg:mb-0 lg:w-1/2'>
            <Image
              src={image_src}
              alt='Hero Image'
              width={616}
              height={684}
              className='mx-auto w-full max-w-md drop-shadow-xl'
            />
          </div>
          <div className='grid grid-cols-2 gap-8 lg:w-1/2'>
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className='flex flex-col items-center text-center'
              >
                <div className='mb-2 text-4xl'>{benefit.icon}</div>
                <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                  {benefit.title}
                </h3>
                <p className='text-sm text-gray-600'>
                  {renderDescription(benefit.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroComp2
